import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "../../../components/Card";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { getLabelById, getUserInfo, SPECIAL_HOBBY_ID, SPECIAL_PETS_ID } from "../../../utilities/helpers";
import { theme } from "../../../styles/theme";
import { Text, Col, Div, Row } from "../../../styles/Common";
import { Input } from "../../../components/Input";
import { Select } from "../../../components/Select";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  GoogleMap,
  MarkerF,
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import { PinIcon, Instagram } from "../../../assets/icons";
import { DatePicker } from "../../../components/DatePicker";
import { Radio } from "../../../components/Radio";
import { Button } from "../../../components/Button";
import { Tag } from "../../../components/Tag";
import mobile from "../../../assets/images/Mobile.png";
import useGetPhoneCode from "../../../core/hooks/useGetPhoneCode";
import useGetDepartmentList from "../../../core/hooks/useGetDepartmentList";
import useGetCityList from "../../../core/hooks/useGetCityList";
import useGetMaritalStatus from "../../../core/hooks/useGetMaritalStatus";
import useGetGender from "../../../core/hooks/useGetGender";
import useGetRole from "../../../core/hooks/useGetRole";
import useGetIncomeRange from "../../../core/hooks/useGetIncomeRange";
import useGetNumberChildren from "../../../core/hooks/useGetNumberChildren";
import useGetPhysicalActivities from "../../../core/hooks/useGetPhysicalActivities";
import useGetPets from "../../../core/hooks/useGetPets";
import useGetHobbies from "../../../core/hooks/useGetHobbies";
import useGetProfessions from "../../../core/hooks/useGetProfessions";
import useGetUserProfile from "../../ProfileInfo/hooks/useGetUserProfile";
import ModalOTPMyAccount from "./ModalOTPMyAccount";
import { setVerificationTokens } from "../../../core/store/verificationCodes";
import useSendEmailOtp from "../../Login/hooks/useSendEmailOtp";
import ModalWarningEdit from "./ModalWarningEdit";
import useEditUserProfile from "../hooks/useEditUserProfile";
import { validateEditUserProfile } from "../../../utilities/validations";
import useValidateUserExistByEmail from "../hooks/useValidateUserExistByEmail";
import useValidateUserExistByPhoneNumber from "../hooks/useValidateUserExistByPhoneNumber";
import { setLocalUser } from "../../../core/services/axiosInstance";
import useGetLevelAcademic from "../../../core/hooks/useGetLevelAcademic";
import { StepProgress } from "./StepProgress";

dayjs.extend(weekday);
dayjs.extend(localeData);

export const EditMyAccount = ({ setEditMyAccount, isMobile }) => {
  const {
    handleSubmit,
    control,
    watch,
    reset,
    trigger,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(validateEditUserProfile),
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [step, setStep] = useState("");

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const sendEmailOtp = useSendEmailOtp();
  const editUserProfile = useEditUserProfile();
  const tokenRequested = useSelector(
    (state) => state.verificationCodes?.verificationTokens
  );

  const validateUserExistByEmail = useValidateUserExistByEmail();
  const validateUserExistByPhoneNumber = useValidateUserExistByPhoneNumber();

  const emailForm = watch("email");
  const watchedFields = watch(["department", "municipality"]);

  const selectedHobbies = watch("hobbies") || [];
  const hasSpecialHobby = selectedHobbies.includes(SPECIAL_HOBBY_ID);
  useEffect(() => {
    if (hasSpecialHobby && selectedHobbies.length > 1) {
      setValue("hobbies", [SPECIAL_HOBBY_ID]);
    }
  }, [hasSpecialHobby, selectedHobbies, setValue]);


  const selectedPets = watch("pets") || [];
  const hasSpecialPets = selectedPets.includes(SPECIAL_PETS_ID);
  useEffect(() => {
    if (hasSpecialPets && selectedPets.length > 1) {
      setValue("pets", [SPECIAL_PETS_ID]);
    }
  }, [hasSpecialPets, selectedPets, setValue]);

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const [coordinates, setCoordinates] = useState({
    lat: 14.6349149,
    lng: -90.5068824,
  });
  const [mapFirstLoad, setMapFirstLoad] = useState(true);
  const [isVerifiedEmail, setIsVerifiedEmail] = useState(true);
  const [dataInitial, setDataInitial] = useState({
    email: "",
    phone: "",
  });
  const [showModalOTP, setShowModalOTP] = useState(false);
  const [showModalWarningEdit, setShowModalWarningEdit] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [confirmEditOtp, setConfirmEditOtp] = useState({
    phone: false,
    email: false,
  });
  const [form, setForm] = useState({});

  const disabledDate = (current) => {
    return (
      current && current.isAfter(dayjs().subtract(18, "years").endOf("day"))
    );
  };

  const handleMarkerDragEnd = async (event) => {
    const { lat, lng } = event.latLng.toJSON();
    setCoordinates({ lat, lng });

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );
    const data = await response.json();
    if (data.results.length > 0) {
      setValue("maps", data.results[0].formatted_address);
    }
  };

  const updateCoordinates = async ({ lat, lng }) => {
    setCoordinates({ lat, lng });

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );
    const data = await response.json();
    if (data.results.length > 0) {
      setValue("maps", data.results[0].formatted_address);
    }
  };

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["places"],
  });
  const [autocomplete, setAutocomplete] = useState(null);

  const onLoad = useCallback((autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const newCoordinates = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        
        const formattedAddress = place.formatted_address || place.name || "";
        setValue("maps", formattedAddress, { shouldValidate: true });
        
        updateCoordinates(newCoordinates);
      }
    }
  }, [autocomplete, updateCoordinates]);

  const { nameid, unique_name } = getUserInfo();
  const getUserProfile = useGetUserProfile(nameid);
  const { data: dataUserProfile, isSuccess: isSuccessUserProfile } =
    getUserProfile;
  useEffect(() => {
    if (isSuccessUserProfile) {
      reset({
        name: dataUserProfile?.data?.result[0]?.name,
        lastName: dataUserProfile?.data?.result[0]?.lastName,
        ind: dataUserProfile?.data?.result[0]?.phoneNumberCode,
        phone: dataUserProfile?.data?.result[0]?.phoneNumber,
        email: dataUserProfile?.data?.result[0]?.email,
        department: dataUserProfile?.data?.result[0]?.departmentId,
        municipality: dataUserProfile?.data?.result[0]?.cityId,
        maps: dataUserProfile?.data?.result[0]?.exactAddress,
        address: dataUserProfile?.data?.result[0]?.address,
        instruction: dataUserProfile?.data?.result[0]?.instruction,
        zone: dataUserProfile?.data?.result[0]?.zone,
        maritalStatus: dataUserProfile?.data?.result[0]?.maritalStatusId,
        gender: dataUserProfile?.data?.result[0]?.genderId,
        birthdate: !!dataUserProfile?.data?.result[0].birthDate
          ? dayjs(dataUserProfile?.data?.result[0]?.birthDate)
          : null,
        role: dataUserProfile?.data?.result[0]?.roleHouseId,
        incomeRange: dataUserProfile?.data?.result[0]?.incomeRangeId,
        numberChildren: dataUserProfile?.data?.result[0]?.numberChildrenId,
        physicalActivities:
          dataUserProfile?.data?.result[0]?.frequencyActivitiesPhysicalId,
        levelAcademic:
          dataUserProfile?.data?.result[0]?.levelAcademicId,
        profession: dataUserProfile?.data?.result[0]?.professionsId,
        instagram: dataUserProfile?.data?.result[0]?.instagram,
        isPregnant: dataUserProfile?.data?.result[0]?.isPregnant,
        isInterestedTechnology:
          dataUserProfile?.data?.result[0]?.isInterestedTechnology,
        householdRole: dataUserProfile?.data?.result[0]?.isBuyManagerHome,
        isAlcoholConsume: dataUserProfile?.data?.result[0]?.isAlcoholConsume,
        isTobaccoConsume: dataUserProfile?.data?.result[0]?.isTobaccoConsume,
        pets: dataUserProfile?.data?.result[0]?.petsId,
        hobbies: dataUserProfile?.data?.result[0]?.hobbiesId,
      });

      setDataInitial({
        email: dataUserProfile?.data?.result[0].email || "",
        phone: dataUserProfile?.data?.result[0].phoneNumber || null,
      });

      if (
        dataUserProfile?.data?.result[0]?.latitude &&
        dataUserProfile?.data?.result[0]?.longitude
      ) {
        setCoordinates({
          lat: Number(dataUserProfile?.data?.result[0]?.latitude),
          lng: Number(dataUserProfile?.data?.result[0]?.longitude),
        });
      }
    }
  }, [dataUserProfile, isSuccessUserProfile]);

  const initials =
    dataUserProfile?.data?.result[0]?.name &&
    dataUserProfile?.data?.result[0]?.lastName
      ? `${dataUserProfile?.data?.result[0]?.name.charAt(
          0
        )}${dataUserProfile?.data?.result[0]?.lastName.charAt(0)}`.toUpperCase()
      : "NN";

  useEffect(() => {
    if (emailForm !== dataInitial.email) {
      setIsVerifiedEmail(false);
    } else {
      setIsVerifiedEmail(true);
    }
  }, [emailForm, dataInitial]);

  const getPhoneCode = useGetPhoneCode();
  const { data, isSuccess } = getPhoneCode;
  const [optionsCodes, setOptionsCodes] = useState([]);
  useEffect(() => {
    if (isSuccess) {
      setOptionsCodes(
        data?.data?.result.map((item) => ({
          label: item.code,
          value: item.id,
        }))
      );
    }
  }, [data, isSuccess]);

  const getDepartmentList = useGetDepartmentList();
  const { data: dataDepartments, isSuccess: isSuccessDepartments } =
    getDepartmentList;
  const [departmentOptions, setDepartmentOptions] = useState([]);
  useEffect(() => {
    if (isSuccessDepartments)
      setDepartmentOptions(
        dataDepartments?.data?.result.map((item) => ({
          label: item.departmentName,
          value: item.id,
        }))
      );
  }, [dataDepartments]);

  const getCityList = useGetCityList(watchedFields[0]);
  const { data: dataCity, isSuccess: isSuccessDataCity } = getCityList;
  const [cityOptions, setCityOptions] = useState([]);
  useEffect(() => {
    if (isSuccessDataCity)
      setCityOptions(
        dataCity?.data?.result.map((item) => ({
          label: item.cityName,
          value: item.id,
        }))
      );
  }, [dataCity]);

  const getMaritalStatus = useGetMaritalStatus();
  const { data: dataMaritalStatus, isSuccess: isSuccessMaritalStatus } =
    getMaritalStatus;
  const [optionsMaritalStatus, setOptionsMaritalStatus] = useState([]);
  useEffect(() => {
    if (isSuccessMaritalStatus) {
      setOptionsMaritalStatus(
        dataMaritalStatus?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataMaritalStatus, isSuccessMaritalStatus]);

  const getGender = useGetGender();
  const { data: dataGender, isSuccess: isSuccessGender } = getGender;
  const [optionsGender, setOptionsGender] = useState([]);
  useEffect(() => {
    if (isSuccessGender) {
      setOptionsGender(
        dataGender?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataGender, isSuccessGender]);

  const getRole = useGetRole();
  const { data: dataRole, isSuccess: isSuccessRole } = getRole;
  const [optionsRole, setOptionsRole] = useState([]);
  useEffect(() => {
    if (isSuccessRole) {
      setOptionsRole(
        dataRole?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataRole, isSuccessRole]);

  const getIncomeRange = useGetIncomeRange();
  const { data: dataIncomeRange, isSuccess: isSuccessIncomeRange } =
    getIncomeRange;
  const [optionsIncomeRange, setOptionsIncomeRange] = useState([]);
  useEffect(() => {
    if (isSuccessIncomeRange) {
      setOptionsIncomeRange(
        dataIncomeRange?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataIncomeRange, isSuccessIncomeRange]);

  const getNumberChildren = useGetNumberChildren();
  const { data: dataNumberChildren, isSuccess: isSuccessNumberChildren } =
    getNumberChildren;
  const [optionsNumberChildren, setOptionsNumberChildren] = useState([]);
  useEffect(() => {
    if (isSuccessNumberChildren) {
      setOptionsNumberChildren(
        dataNumberChildren?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataNumberChildren, isSuccessNumberChildren]);

  const getPhysicalActivities = useGetPhysicalActivities();
  const {
    data: dataPhysicalActivities,
    isSuccess: isSuccessPhysicalActivities,
  } = getPhysicalActivities;
  const [optionsPhysicalActivities, setOptionsPhysicalActivities] = useState(
    []
  );
  useEffect(() => {
    if (isSuccessPhysicalActivities) {
      setOptionsPhysicalActivities(
        dataPhysicalActivities?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataPhysicalActivities, isSuccessPhysicalActivities]);

  const getLevelAcademic = useGetLevelAcademic();
  const {
    data: dataLevelAcademic,
    isSuccess: isSuccessLevelAcademic,
  } = getLevelAcademic;
  const [optionsLevelAcademic, setOptionsLevelAcademic] = useState(
    []
  );
  useEffect(() => {
    if (isSuccessLevelAcademic) {
      setOptionsLevelAcademic(
        dataLevelAcademic?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataLevelAcademic, isSuccessLevelAcademic]);

  const getPets = useGetPets();
  const { data: dataPets, isSuccess: isSuccessPets } = getPets;
  const [optionsPets, setOptionsPets] = useState([]);
  useEffect(() => {
    if (isSuccessPets) {
      setOptionsPets(
        dataPets?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataPets, isSuccessPets]);

  const getHobies = useGetHobbies();
  const { data: dataHobbies, isSuccess: isSuccessHobbies } = getHobies;
  const [optionsHobbies, setOptionsHobbies] = useState([]);
  useEffect(() => {
    if (isSuccessHobbies) {
      setOptionsHobbies(
        dataHobbies?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataHobbies, isSuccessHobbies]);

  const getProfessions = useGetProfessions();
  const { data: dataProfessions, isSuccess: isSuccessProfessions } =
    getProfessions;
  const [optionsProfessions, setOptionsProfessions] = useState([]);
  useEffect(() => {
    if (isSuccessProfessions) {
      const groupedProfessions = _.groupBy(dataProfessions.data.result, "code");
      const formattedOptions = Object.keys(groupedProfessions).map((code) => ({
        label: code,
        options: groupedProfessions[code].map((item) => ({
          label: item.name,
          value: item.id,
        })),
      }));

      setOptionsProfessions(formattedOptions);
    }
  }, [dataProfessions, isSuccessProfessions]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (mapFirstLoad) {
      setMapFirstLoad(false);
      return;
    }
    const city = getLabelById(watchedFields[1], cityOptions);
    const department = getLabelById(watchedFields[0], departmentOptions);

    if (city && department) {
      const fetchCoordinates = async () => {
        const address = `${city}, ${department}`;
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            address
          )}&key=${apiKey}`
        );
        const data = await response.json();
        if (data.results.length > 0) {
          const { lat, lng } = data.results[0].geometry.location;
          setCoordinates({ lat, lng });
        }
      };

      fetchCoordinates();
    }
  }, [watchedFields[0], watchedFields[1]]);

  const handleClose = (removedTag, value) => {
    const currentValues = watch(value);
    const newHobbies = currentValues.filter((item) => item !== removedTag);

    setValue(value, newHobbies);
  };

  const handleCloseModal = () => {
    setShowModalOTP(false);
    setShowModalWarningEdit(false);
  };

  const onError = (errors) => {
    toast.error(`Hay campos requeridos en el formulario. Por favor, revísalos.`);
  };

  const handleSave = async (data) => {
    if (!isVerifiedEmail) {
      const emailAction = !isVerifiedEmail ? sendEmailOtp : null;

      const emailData = !isVerifiedEmail
        ? { email: data.email, userName: unique_name, isPasswordReset: false }
        : null;

      const handleSuccess = (res, type) => {
        if (type === "email") {
          dispatch(
            setVerificationTokens({
              tokenEmail: res?.data?.result,
              tokenPhone: tokenRequested?.tokenPhone,
            })
          );
        }
      };

      const checkEmail = async () => {
        try {
          const res = await validateUserExistByEmail.mutateAsync({
            userId: Number(nameid),
            userEmail: data.email,
          });
          if (res?.data.result) {
            toast.error("Correo ya registrado");
            return false;
          }
          return true;
        } catch (err) {
          toast.error(err?.response?.data?.title || err?.response?.data);
          return false;
        }
      };

      try {
        const [emailAvailable] = await Promise.all([checkEmail()]);

        if (emailAvailable) {
          if (emailAction) {
            emailAction.reset();
            await emailAction.mutateAsync(emailData, {
              onSuccess: (res) => handleSuccess(res, "email"),
              onError: (err) => {
                toast.error(err?.response?.data?.title || err?.response?.data);
              },
            });
          }
          setShowModalOTP(true);
          setForm({
            ...data,
            nameid: nameid,
            lat: String(coordinates.lat),
            lng: String(coordinates.lng),
          });
        }
      } catch (err) {
        toast.error("Hubo un error al verificar los datos. Intente de nuevo.");
      }
    } else {
      editUserProfile.reset();
      editUserProfile.mutate(
        {
          userId: Number(nameid),
          maritalStatusId: data.maritalStatus,
          genderId: data.gender,
          roleHouseId: data.role,
          cityId: Number(data.municipality),
          address: data.address,
          instruction: data.instruction,
          zone: data.zone,
          phoneNumber: data.phone,
          name: data.name,
          email: data.email,
          lastName: data.lastName,
          birthDate: data.birthdate,
          phoneCodeId: Number(data.ind),
          departmentId: Number(data.department),
          exactAddress: data.maps,
          latitude: String(coordinates.lat),
          longitude: String(coordinates.lng),
          profileImageUrl: null, //pending
          incomeRangeId: data.incomeRange,
          numberChildrenId: data.numberChildren,
          professionsId: data.profession,
          frequencyActivitiesPhysicalId: data.physicalActivities,
          levelAcademicId: data.levelAcademic,
          isBuyManagerHome: data.householdRole,
          isAlcoholConsume: data.isAlcoholConsume,
          isTobaccoConsume: data.isTobaccoConsume,
          isPregnant: data.isPregnant,
          isInterestedTechnology: data.isInterestedTechnology,
          hobbiesId: data.hobbies ? data.hobbies.filter((item) => item) : null,
          petsId: data.pets ? data.pets.filter((item) => item) : null,
          instagram: data.instagram,
        },
        {
          onSuccess: (res) => {
            queryClient.invalidateQueries(["getUserProfile"]);
            toast.success("Cambios guardados correctamente");
            setMapFirstLoad(true);
            setLocalUser(res?.data?.result);
            setEditMyAccount(false);
          },
          onError: (err) => {
            if (err?.response?.data?.errors) {
              const errorMessages = Object.values(err.response.data.errors)
                .flat()
                .join(", ");
              toast.error(errorMessages);
            }
          },
        }
      );
    }
  };

  return (
    <>
      <ModalOTPMyAccount
        showModalOTP={showModalOTP}
        handleCloseModal={handleCloseModal}
        form={form}
        isVerifiedEmail={isVerifiedEmail}
        setEditMyAccount={setEditMyAccount}
        isMobile={isMobile}
      />
      <ModalWarningEdit
        showModal={showModalWarningEdit}
        handleCloseModal={handleCloseModal}
        confirmEditOtp={confirmEditOtp}
        setConfirmEditOtp={setConfirmEditOtp}
        input={selectedInput}
        isMobile={isMobile}
      />
      <Card
        width={!isMobile ? "610px" : "326px"}
        height={"auto"}
        p="32.5px 37.5px 34px 37px"
        m="0 0 150px 0"
      >
        <StepProgress setStep={setStep} currentStep={currentStep} setCurrentStep={setCurrentStep} />
        {currentStep === 1 && (
          <Col align="center" justify="center">
           <Text
              size={theme.fonts.size.h4}
              weight={theme.fonts.weight.bold}
              color={theme.colors.white}
              mb="24px"
            >
              {step}
            </Text>
            <Div
              height="80px"
              width="80px"
              radius="50px"
              background={theme.colors.red}
              align="center"
              justify="center"
              m="0 0 32.5px 0"
            >
              <Text
                size={theme.fonts.size.h3}
                weight={theme.fonts.weight.bold}
                color={theme.colors.white}
              >
                {initials}
              </Text>
            </Div>
            {/* <Text size={theme.fonts.size.sm} color={theme.colors.red} mb="32.5px">
              Cambiar foto
            </Text> */}
            <Div
              gap="19px"
              width="100%"
              m="0 0 24.2px 0"
              direction={!isMobile ? "row" : "column"}
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Nombre"
                    colorLabel={theme.colors.gray200}
                    sizeLabel={theme.fonts.size.default}
                    border={theme.colors.white200}
                    width={!isMobile ? "258px" : "100%"}
                    height="45px"
                    background={theme.colors.blue100}
                    error={errors?.name}
                    helper={errors?.name?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Apellidos"
                    colorLabel={theme.colors.gray200}
                    sizeLabel={theme.fonts.size.default}
                    border={theme.colors.white200}
                    width={!isMobile ? "258px" : "100%"}
                    height="45px"
                    background={theme.colors.blue100}
                    error={errors?.lastName}
                    helper={errors?.lastName?.message}
                    {...field}
                  />
                )}
              />
            </Div>
            <Div
              m="0 0 24.2px 0"
              width="100%"
              gap={isMobile && "19px"}
              direction={!isMobile ? "row" : "column"}
            >
              <Row gap="6px">
                <Controller
                  name="ind"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Indicativo"
                      color={theme.colors.gray200}
                      border={theme.colors.white200}
                      options={optionsCodes}
                      width={!isMobile ? "78.5px" : "100%"}
                      height="45px"
                      style={{ padding: "0px 2px" }}
                      background={theme.colors.blue100}
                      error={errors?.ind}
                      helper={errors?.ind?.message}
                      m="0 10px 0 0"
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={"Número de teléfono"}
                      colorLabel={theme.colors.gray200}
                      sizeLabel={theme.fonts.size.default}
                      width={!isMobile ? "168px" : "100%"}
                      error={errors.phone}
                      margin={!isMobile && "0 18px 0 0"}
                      helper={errors.phone ? errors.phone.message : ""}
                      {...field}
                    />
                  )}
                />
              </Row>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    label={"Correo electrónico"}
                    colorLabel={theme.colors.gray200}
                    sizeLabel={theme.fonts.size.default}
                    width={!isMobile ? "258px" : "100%"}
                    error={errors.email}
                    helper={errors.email ? errors.email.message : ""}
                    onFocus={() => {
                      if (!confirmEditOtp?.email) {
                        setSelectedInput("email");
                        setShowModalWarningEdit(true);
                      }
                    }}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value.toLowerCase());
                    }}
                  />
                )}
              />
            </Div>
            <Div
              gap="19px"
              m="0 0 24.4px 0"
              width="100%"
              direction={!isMobile ? "row" : "column"}
            >
              <Controller
                name="maritalStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Estado Civil"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    width={!isMobile ? "258px" : "100%"}
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsMaritalStatus}
                    error={errors?.maritalStatus}
                    helper={errors?.maritalStatus?.message}
                    {...field}
                  />
                )}
              />

              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Género"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    width={!isMobile ? "258px" : "100%"}
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsGender}
                    error={errors?.gender}
                    helper={errors?.gender?.message}
                    {...field}
                  />
                )}
              />
            </Div>
          </Col>
        )}
        {currentStep === 2 && (
          <Col align="center" justify="center">
           <Text
              size={theme.fonts.size.h4}
              weight={theme.fonts.weight.bold}
              color={theme.colors.white}
              mb="24px"
            >
              {step}
            </Text>
            <Div gap="19px" direction={!isMobile ? "row" : "column"}>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label={"Departamento"}
                    width={"258px"}
                    height={"45px"}
                    options={departmentOptions}
                    background={theme.colors.blue100}
                    error={errors.department}
                    helper={errors.department ? errors.department.message : ""}
                    onChange={(e) => {
                      field.onChange(e);
                      setValue("municipality", null);
                      queryClient.invalidateQueries(["getCityList", e]);
                    }}
                  />
                )}
              />
              <Controller
                name="municipality"
                control={control}
                render={({ field }) => (
                  <Select
                    label={"Municipio"}
                    width={"258px"}
                    height={"45px"}
                    options={cityOptions}
                    background={theme.colors.blue100}
                    error={errors.municipality}
                    helper={
                      errors.municipality ? errors.municipality.message : ""
                    }
                    {...field}
                  />
                )}
              />
            </Div>
            <Div
              m="0 0 15px 0"
              align="left"
              direction={!isMobile ? "row" : "column"}
            >
              <Div
                width={!isMobile ? "536px" : "100%"}
                height="194px"
                background={theme.colors.white}
                radius="22px"
                m="20px 0 0 0"
                style={{ overflow: "hidden" }}
              >
                {isLoaded && (
                  <GoogleMap
                    mapContainerStyle={{
                      height: "194px",
                      width: !isMobile ? "536px" : "100%",
                    }}
                    center={
                      coordinates.lat !== 0 && coordinates.lng !== 0
                        ? coordinates
                        : { lat: 14.6349149, lng: -90.5068824 }
                    }
                    zoom={15}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: true,
                      zoomControl: true,
                    }}
                    onClick={handleMarkerDragEnd}
                  >
                    <Controller
                      name="maps"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          onLoad={onLoad}
                          onPlaceChanged={onPlaceChanged}
                        >
                          <Input
                            placeholder={"Ubica tu dirección en el mapa"}
                            colorLabel={theme.colors.gray200}
                            sizeLabel={theme.fonts.size.default}
                            border={theme.colors.gray200}
                            style={{ padding: "0px 20px", margin: "7.5px 9.5px" }}
                            width={!isMobile ? "468px" : "100%"}
                            background={theme.colors.white}
                            error={errors.maps}
                            helper={errors.maps ? errors.maps.message : ""}
                            suffix={
                              <PinIcon
                                stroke={theme.colors.gray200}
                                width={"24px"}
                                height={"24px"}
                              />
                            }
                            {...field}
                          />
                        </Autocomplete>
                      )}
                    />
                    <MarkerF
                      position={coordinates}
                      draggable={true}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  </GoogleMap>
                )}
              </Div>
            </Div>
            {errors?.maps && (
              <Text style={{alignSelf: 'start'}} size={theme.fonts.size.xs} color={theme.colors.red} mb="15px">
                {errors.maps.message}
              </Text>
            )}
            <Col gap="8px" align="left">
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <Input
                    label={"Dirección"}
                    colorLabel={theme.colors.gray200}
                    sizeLabel={theme.fonts.size.default}
                    style={{ padding: "0px 20px" }}
                    width={!isMobile ? "535.5px" : "100%"}
                    background={theme.colors.blue100}
                    error={errors.address}
                    helper={errors.address ? errors.address.message : ""}
                    suffix={
                      <PinIcon
                        stroke={theme.colors.white}
                        width={"24px"}
                        height={"24px"}
                      />
                    }
                    {...field}
                  />
                )}
              />
              <Text
                size={theme.fonts.size.sm}
                color={theme.colors.gray200}
                weight={theme.fonts.weight.bold}
                style={{ padding: "0 10px" }}
                mb="24px"
              >
                Por favor, especifica de forma exacta todos los detalles de tu dirección, 
                incluyendo colonia, caserío, aldea, piso, apartamento o número de casa. La 
                ubicación en el mapa también es necesaria para obtener una referencia visual de tu ubicación.
              </Text>
            </Col>
            <Col align="left" gap="8px" m=" 0 0 24px 0">
              <Controller
                name="instruction"
                control={control}
                render={({ field }) => (
                  <Input
                    label={"Indicaciones"}
                    colorLabel={theme.colors.gray200}
                    sizeLabel={theme.fonts.size.default}
                    style={{ padding: "0px 20px" }}
                    width={!isMobile ? "535.5px" : "100%"}
                    background={theme.colors.blue100}
                    {...field}
                  />
                )}
              />
              <Text
                size={theme.fonts.size.sm}
                color={theme.colors.gray200}
                style={{ padding: "0 10px" }}
                mb="15px"
              >
                Tú información de residencia es muy importante para que podamos
                enviarte la PulpeyBox a la puerta de tu casa.
              </Text>
              <Controller
                name="zone"
                control={control}
                render={({ field }) => (
                  <Input
                    label={"Zona"}
                    colorLabel={theme.colors.gray200}
                    sizeLabel={theme.fonts.size.default}
                    style={{ padding: "0px 20px" }}
                    width={!isMobile ? "535.5px" : "100%"}
                    background={theme.colors.blue100}
                    error={errors.zone}
                    helper={errors.zone ? errors.zone.message : ""}
                    inputMode="numeric"
                    maxLength={2}
                    min={0}
                    onInput={(e) => {
                      const inputValue = e.target.value;
                      const filteredValue = inputValue.replace(/[^0-9]/g, "");
                      if (filteredValue.length <= 2) {
                        e.target.value = filteredValue;
                      } else {
                        e.target.value = filteredValue.slice(0, 2);
                      }
                    }}
                    {...field}
                  />
                )}
              />
            </Col>
          </Col>
        )}
        {currentStep === 3 && (
          <Col align="center" justify="center">
            <Text
              size={theme.fonts.size.h4}
              weight={theme.fonts.weight.bold}
              color={theme.colors.white}
              mb="24px"
            >
              {step}
            </Text>    
            <Div
              gap="19px"
              m="0 0 24px 0"
              direction={!isMobile ? "row" : "column"}
            >
              <Controller
                name="birthdate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Fecha de nacimiento"
                    placeholder={false}
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    width={!isMobile ? "258px" : "100%"}
                    height="45px"
                    format="DD/MM/YYYY"
                    background={theme.colors.blue100}
                    error={errors?.birthdate}
                    helper={errors?.birthdate?.message}
                    disabledDate={disabledDate}
                    dateRender={(current) => {
                      const isDisabled = disabledDate(current);
                      const isSelected =
                        field.value && current.isSame(dayjs(field.value), "day");
                      return (
                        <div
                          style={{
                            display: isDisabled ? "none" : "block",
                            background: isSelected ? theme.colors.red : "none",
                          }}
                        >
                          {current.date()}
                        </div>
                      );
                    }}
                    {...field}
                  />
                )}
              />
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Rol de tu casa"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    width="258px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsRole}
                    error={errors?.role}
                    helper={errors?.role?.message}
                    {...field}
                  />
                )}
              />
            </Div>
            <Div
              gap="19px"
              m="0 0 24px 0"
              direction={!isMobile ? "row" : "column"}
            >
              <Controller
                name="incomeRange"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Rango de ingresos"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    width="258px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsIncomeRange}
                    {...field}
                  />
                )}
              />
              <Controller
                name="numberChildren"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Cantidad de hijos"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    error={errors.numberChildren}
                    helper={errors.numberChildren ? errors.numberChildren.message : ""}
                    width="258px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsNumberChildren}
                    {...field}
                  />
                )}
              />
            </Div>
            <Div
              gap="19px"
              m="0 0 24px 0"
              direction={!isMobile ? "row" : "column"}
            >
              <Controller
                name="physicalActivities"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Frecuencia actividades físicas"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    error={errors.physicalActivities}
                    helper={errors.physicalActivities ? errors.physicalActivities.message : ""}
                    width="258px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsPhysicalActivities}
                    {...field}
                  />
                )}
              />

              <Controller
                name="profession"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Profesión u oficio"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    error={errors.profession}
                    helper={errors.profession ? errors.profession.message : ""}
                    width="258px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsProfessions}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    {...field}
                  />
                )}
              />
            </Div>
            <Div
              m="0 0 23px 0"
              gap="20px"
              width="100%"
              direction={!isMobile ? "row" : "column"}
            >
              <Controller
                name="levelAcademic"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Nivel académico"
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    error={errors.levelAcademic}
                    helper={errors.levelAcademic ? errors.levelAcademic.message : ""}
                    width="258px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsLevelAcademic}
                    {...field}
                  />
                )}
              />
              <Controller
                name="instagram"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Perfil de Instagram"
                    sizeLabel={theme.fonts.size.default}
                    colorLabel={theme.colors.gray200}
                    color={theme.colors.gray200}
                    border={theme.colors.white200}
                    style={{ padding: "5px 12px" }}
                    width="259px"
                    height="45px"
                    background={theme.colors.blue100}
                    options={optionsProfessions}
                    suffix={
                      <Instagram
                        stroke={theme.colors.gray200}
                        width={"24px"}
                        height={"24px"}
                      />
                    }
                    {...field}
                  />
                )}
              />
            </Div>
          </Col>
        )}
        {currentStep === 4 && (
          <Col align="center" justify="center">
           <Text
              size={theme.fonts.size.h4}
              weight={theme.fonts.weight.bold}
              color={theme.colors.white}
              mb="24px"
            >
              {step}
            </Text>
            <Div
              m="0 0 23px 0"
              gap="20px"
              width="100%"
              direction={!isMobile ? "row" : "column"}
            >
              <Col gap="4px" width={!isMobile? "50%": "100%"}>
                <Text color={theme.colors.gray200} align="left">
                  {" "}
                  ¿Te interesa la innovación tecnológica?
                </Text>
                <Controller
                  name="isInterestedTechnology"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Radio.Group {...field} value={field.value} gap="48px">
                        <Radio
                          value={true}
                          label="Si"
                          background={theme.colors.blue100}
                        />
                        <Radio
                          value={false}
                          label="No"
                          background={theme.colors.blue100}
                        />
                      </Radio.Group>
                      {error && (
                        <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                          {error.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>

              <Col gap="4px" width={!isMobile? "50%": "100%"}>
                <Text color={theme.colors.gray200} align="left">
                  ¿Tomas el rol de las compras del hogar?
                </Text>
                <Controller
                  name="householdRole"
                  control={control}
                  error={errors.householdRole}
                  helper={errors.householdRole ? errors.householdRole.message : ""}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Radio.Group {...field} value={field.value} gap="48px">
                        <Radio
                          value={true}
                          label="Si"
                          background={theme.colors.blue100}
                        />
                        <Radio
                          value={false}
                          label="No"
                          background={theme.colors.blue100}
                        />
                      </Radio.Group>
                      {error && (
                        <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                          {error.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Div>
            <Div
              m="0 0 23px 0"
              gap="20px"
              width="100%"
              direction={!isMobile ? "row" : "column"}
            >
              <Col gap="4px" width={!isMobile? "50%": "100%"}>
                <Text color={theme.colors.gray200} align="left">
                  {" "}
                  ¿Consumes nicotina?
                </Text>
                <Controller
                  name="isTobaccoConsume"
                  control={control}
                  error={errors.isTobaccoConsume}
                  helper={errors.isTobaccoConsume ? errors.isTobaccoConsume.message : ""}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Radio.Group {...field} value={field.value} gap="48px">
                        <Radio
                          value={true}
                          label="Si"
                          background={theme.colors.blue100}
                        />
                        <Radio
                          value={false}
                          label="No"
                          background={theme.colors.blue100}
                        />
                      </Radio.Group>
                      {error && (
                        <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                          {error.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>

              <Col gap="4px" width={!isMobile? "50%": "100%"}>
                <Text color={theme.colors.gray200} align="left">
                  ¿Consumes alcohol?
                </Text>
                <Controller
                  name="isAlcoholConsume"
                  control={control}
                  error={errors.isAlcoholConsume}
                  helper={errors.isAlcoholConsume ? errors.isAlcoholConsume.message : ""}
                  render={({ field, fieldState: {error} }) => (
                    <>
                      <Radio.Group {...field} value={field.value} gap="48px">
                        <Radio
                          value={true}
                          label="Si"
                          background={theme.colors.blue100}
                        />
                        <Radio
                          value={false}
                          label="No"
                          background={theme.colors.blue100}
                        />
                      </Radio.Group>
                      {error && (
                        <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                          {error.message}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Div>
            <Div
              m="0 0 23px 0"
              gap="20px"
              width="100%"
              direction={!isMobile ? "row" : "column"}
            >
              <Col gap="4px">
                <Text color={theme.colors.gray200} align="left">
                  {dataUserProfile?.data?.result[0]?.genderId == 6? "¿Te encuentras embarazada?": "¿Tu pareja se encuentra embarazada?"}
                </Text>
                <Controller
                  name="isPregnant"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group {...field} value={field.value} gap="48px">
                      <Radio
                        value={true}
                        label="Si"
                        background={theme.colors.blue100}
                      />
                      <Radio
                        value={false}
                        label="No"
                        background={theme.colors.blue100}
                      />
                    </Radio.Group>
                  )}
                />
              </Col>
            </Div>
            <Row width="100%">
              <Col width="100%">
                <Controller
                  name="pets"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Mascotas"
                      mode="multiple"
                      color={theme.colors.gray200}
                      border={theme.colors.white200}
                      width={!isMobile ? "535px" : "100%"}
                      height="45px"
                      background={theme.colors.blue100}
                      options={optionsPets.map(option => ({
                        ...option,
                        disabled: hasSpecialPets && option.value !== SPECIAL_PETS_ID
                      }))}
                      onChange={(value) => {
                        const addingSpecialPets = 
                          value.includes(SPECIAL_PETS_ID) && 
                          !selectedPets.includes(SPECIAL_PETS_ID);
                        
                        if (addingSpecialPets) {
                          field.onChange([SPECIAL_PETS_ID]);
                        } else {
                          field.onChange(value);
                        }
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      value={null}
                      tagRender={() => null}
                      {...field}
                    />
                  )}
                />{" "}
                <Row style={{ flexWrap: "wrap" }} m="10.4px 0 32.5px 0">
                  {selectedPets &&
                    selectedPets.length > 0 &&
                    selectedPets
                      .filter((item) => item)
                      .map((item, index) => (
                        <Tag
                          key={index}
                          closable
                          onClose={() => handleClose(item, "pets")}
                        >
                          {getLabelById(item, optionsPets)}
                        </Tag>
                      ))}
                </Row>{" "}
              </Col>
            </Row>
            <Row width="100%">
              <Col width="100%">
                <Controller
                  name="hobbies"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Hobbies"
                      mode="multiple"
                      color={theme.colors.gray200}
                      border={theme.colors.white200}
                      width={!isMobile ? "535px" : "100%"}
                      height="45px"
                      background={theme.colors.blue100}
                      error={errors.hobbies}
                      helper={errors.hobbies ? errors.hobbies.message : ""}
                      options={optionsHobbies.map(option => ({
                        ...option,
                        disabled: hasSpecialHobby && option.value !== SPECIAL_HOBBY_ID
                      }))}
                      onChange={(value) => {
                        const addingSpecialHobby = 
                          value.includes(SPECIAL_HOBBY_ID) && 
                          !selectedHobbies.includes(SPECIAL_HOBBY_ID);
                        
                        if (addingSpecialHobby) {
                          field.onChange([SPECIAL_HOBBY_ID]);
                        } else {
                          field.onChange(value);
                        }
                      }}
                      value={null}
                      tagRender={() => null}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      {...field}
                    />
                  )}
                />

                <Row style={{ flexWrap: "wrap" }} m="14.5px 0 59.7px 0">
                  {selectedHobbies &&
                    selectedHobbies.length > 0 &&
                    selectedHobbies
                      .filter((item) => item)
                      .map((item, index) => (
                        <Tag
                          key={index}
                          closable
                          onClose={() => handleClose(item, "hobbies")}
                        >
                          {getLabelById(item, optionsHobbies)}
                        </Tag>
                      ))}
                </Row>
              </Col>
            </Row>
          </Col>
        )}
        <Col align="center" justify="center">
          <Col gap="28px">
            {currentStep === 4 && <form onSubmit={handleSubmit(handleSave, onError)}>
              <Button
                width={!isMobile ? "318px" : "190px"}
                htmlType="submit"
                background={theme.colors.red}
                loading={
                  validateUserExistByEmail.isLoading ||
                  validateUserExistByPhoneNumber.isLoading ||
                  sendEmailOtp.isLoading ||
                  editUserProfile.isLoading
                }
              >
                Guardar cambios
              </Button>
            </form>}
            {currentStep < 4 && (
              <Button
                width={!isMobile ? "318px" : "190px"}
                htmlType="button"
                background={theme.colors.red}
                onClick={async () => {
                  const fieldsByStep = {
                    1: ['name', 'lastName', 'ind', 'phone', 'gender', 'maritalStatus', 'email'],
                    2: ['department', 'municipality', 'maps', 'address', 'zone'],
                    3: ['birthdate', 'role', 'numberChildren', 'physicalActivities', 'profession', 'levelAcademic'],
                  };

                  const fieldsToValidate = fieldsByStep[currentStep] || [];

                  const isValid = await trigger(fieldsToValidate);

                  if (!isValid) {
                    console.log("Errores de validación", errors, fieldsToValidate, );
                    return;
                  }

                  setCurrentStep(currentStep + 1);
                  window.scrollTo(0, 0);
                }}
                loading={
                  validateUserExistByEmail.isLoading ||
                  validateUserExistByPhoneNumber.isLoading ||
                  sendEmailOtp.isLoading ||
                  editUserProfile.isLoading
                }
              >
                Siguiente
              </Button>
            )}

            <Text
              style={{ cursor: "pointer" }}
              weight={theme.fonts.weight.bold}
              color={theme.colors.white}
              onClick={() => setEditMyAccount(false)}
            >
              Cancelar
            </Text>
          </Col>
        </Col>
      </Card>
      {!isMobile && (
        <img
          src={mobile}
          alt="image mobile"
          width={"669px"}
          height={"417px"}
          style={{ position: "absolute", left: "0", bottom: "0", zIndex: -1 }}
        />
      )}
    </>
  );
};

EditMyAccount.propTypes = {
  setEditMyAccount: PropTypes.func,
  isMobile: PropTypes.bool,
};
